import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  useGetCoinExStatusQuery,
  useSendCoinExVerificationMutation,
  useVerifyCoinExEmailMutation,
} from '../../store/api/balanceApi';
import { ButtonSpinner } from '../ui/Loader';

enum CoinExStep {
  CHECKING,
  ENTER_EMAIL,
  VERIFY_CODE,
  READY,
}

export function CoinExClaimForm() {
  const { t } = useTranslation();
  const { data: statusData, isFetching: isStatusLoading, refetch: refetchStatus } = useGetCoinExStatusQuery();
  const [sendVerification, { isLoading: isSending }] = useSendCoinExVerificationMutation();
  const [verifyEmail, { isLoading: isVerifying }] = useVerifyCoinExEmailMutation();

  const [step, setStep] = useState(CoinExStep.CHECKING);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const coinexEmail = statusData?.data?.coinex_email;
  const adminEmail = statusData?.data?.adminEmail;
  const coin = statusData?.data?.coin || 'USDT';
  const minAmount = statusData?.data?.minAmount;

  useEffect(() => {
    if (!isStatusLoading && statusData) {
      if (coinexEmail) {
        setStep(CoinExStep.READY);
      } else if (adminEmail) {
        setStep(CoinExStep.ENTER_EMAIL);
      }
    }
  }, [isStatusLoading, statusData, coinexEmail, adminEmail]);

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email.trim() || !email.includes('@')) {
      setError(t('recharge.coinexClaim.emailRequired'));
      return;
    }

    try {
      await sendVerification({ email: email.trim().toLowerCase() }).unwrap();
      toast.success(t('recharge.coinexClaim.sendCode'));
      setStep(CoinExStep.VERIFY_CODE);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setError(error?.data?.message || error?.message || t('recharge.coinexClaim.sendFailed'));
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!code.trim()) {
      setError(t('recharge.coinexClaim.codeRequired'));
      return;
    }

    try {
      await verifyEmail({ email: email.trim().toLowerCase(), code: code.trim() }).unwrap();
      toast.success(t('recharge.coinexClaim.verified'));
      await refetchStatus();
      setStep(CoinExStep.READY);
    } catch (err) {
      const error = err as { data?: { message?: string }; message?: string };
      setError(error?.data?.message || error?.message || t('recharge.coinexClaim.verifyFailed'));
    }
  };

  const handleCopyEmail = async () => {
    if (!adminEmail) return;
    try {
      await navigator.clipboard.writeText(adminEmail);
      toast.success(t('common.copied'));
    } catch {
      toast.error(t('common.copyError'));
    }
  };

  if (isStatusLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <ButtonSpinner />
        <span className="ml-2 text-neutral-medium">{t('common.loading')}</span>
      </div>
    );
  }

  if (!adminEmail) {
    return (
      <div className="bg-neutral-dark/30 rounded-lg p-4">
        <p className="text-sm text-neutral-medium">{t('recharge.coinexClaim.notConfigured')}</p>
      </div>
    );
  }

  if (step === CoinExStep.ENTER_EMAIL) {
    return (
      <form onSubmit={(e) => { void handleSendCode(e); }} className="space-y-6" noValidate>
        <div className="bg-neutral-dark/30 rounded-lg p-4">
          <p className="text-sm text-neutral-medium font-body">
            {t('recharge.coinexClaim.enterEmailDescription')}
          </p>
        </div>

        <div>
          <label htmlFor="coinexEmail" className="block text-sm font-medium text-neutral-light mb-2 font-body">
            {t('recharge.coinexClaim.emailLabel')} <span className="text-danger">*</span>
          </label>
          <input
            id="coinexEmail"
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(null); }}
            disabled={isSending}
              placeholder={t('recharge.coinexClaim.emailPlaceholder')}
            className={`w-full px-4 py-3 card-glass rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body ${error ? 'border-danger' : 'border-neutral-dark'}`}
          />
          {error && <p className="mt-1 text-sm text-danger">{error}</p>}
        </div>

        <button
          type="submit"
          disabled={isSending || !email.trim()}
          className="w-full px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSending ? (
            <><ButtonSpinner /><span>{t('common.loading')}</span></>
          ) : (
            t('recharge.coinexClaim.sendCode')
          )}
        </button>
      </form>
    );
  }

  if (step === CoinExStep.VERIFY_CODE) {
    return (
      <form onSubmit={(e) => { void handleVerifyCode(e); }} className="space-y-6" noValidate>
        <div className="bg-neutral-dark/30 rounded-lg p-4">
          <p className="text-sm text-neutral-medium font-body">
            {t('recharge.coinexClaim.codeSent')} <strong>{email}</strong>
          </p>
        </div>

        <div>
          <label htmlFor="coinexCode" className="block text-sm font-medium text-neutral-light mb-2 font-body">
            {t('recharge.coinexClaim.codeLabel')} <span className="text-danger">*</span>
          </label>
          <input
            id="coinexCode"
            type="text"
            value={code}
            onChange={(e) => { setCode(e.target.value); setError(null); }}
            disabled={isVerifying}
            placeholder={t('recharge.coinexClaim.codePlaceholder')}
            className={`w-full px-4 py-3 card-glass rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-300 font-body ${error ? 'border-danger' : 'border-neutral-dark'}`}
          />
          {error && <p className="mt-1 text-sm text-danger">{error}</p>}
        </div>

        <button
          type="submit"
          disabled={isVerifying || !code.trim()}
          className="w-full px-6 py-3 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isVerifying ? (
            <><ButtonSpinner /><span>{t('common.loading')}</span></>
          ) : (
            t('recharge.coinexClaim.verifyEmail')
          )}
        </button>
      </form>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-neutral-dark/30 rounded-lg p-4">
        <p className="text-sm text-neutral-medium font-body">
          {t('recharge.coinexClaim.description')}
        </p>
      </div>

      <div className="bg-neutral-dark/30 rounded-lg p-4 space-y-3">
        <h3 className="text-base font-heading font-semibold text-brand-accent">
          {t('recharge.coinexClaim.instructionsTitle')}
        </h3>

        <div className="space-y-2">
          <span className="text-neutral-medium font-body text-sm block">
            {t('recharge.coinexClaim.adminEmailLabel')}
          </span>
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-2 bg-neutral-dark/50 rounded-lg">
            <span className="text-neutral-light font-mono text-sm break-all">
              {adminEmail}
            </span>
            <button
              type="button"
              onClick={() => void handleCopyEmail()}
              className="p-1.5 bg-brand-accent/20 text-brand-accent rounded hover:bg-brand-accent/30 transition-colors flex-shrink-0 self-end sm:self-auto"
              title={t('common.copy')}
              aria-label={t('common.copy')}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
            </button>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:justify-between gap-2">
          <span className="text-neutral-medium font-body text-sm">{t('recharge.currency')}:</span>
          <span className="text-neutral-light font-body text-sm">{coin}</span>
        </div>

        <p className="text-xs text-neutral-medium font-body">
          {t('recharge.minAmount')}: {minAmount || 1} {coin}
        </p>

        <p className="text-xs text-neutral-medium font-body mt-2">
          <strong>{t('recharge.coinexClaim.yourEmailLabel')}</strong> {coinexEmail}
        </p>
      </div>

      <div className="bg-brand-accent/10 border border-brand-accent/30 rounded-lg p-4">
        <p className="text-sm text-neutral-light font-body">
          {t('recharge.coinexClaim.depositInfo')}
        </p>
      </div>
    </div>
  );
}